import { Link } from "react-router-dom"
import { motion } from "motion/react"
import { CinemaAction } from "../../types/entities/action"
import BannerAdDisclosure, { hasBannerAdInfo } from "./BannerAdDisclosure"
import { useCinemaHref } from "../../hooks/useCinemaHref"
import { actionPath } from "../../lib/actionSlug"
import { AfishaBanner } from "../../types/entities/banner"

const DISPLAY = "'Tilda Sans', sans-serif"
const BODY = "'Manrope', sans-serif"

function actionAsBanner(action: CinemaAction): AfishaBanner {
    return {
        id: action.id,
        image: action.image,
        title: action.title,
        erid: action.erid ?? undefined,
        legal_entity_inn: action.legal_entity_inn ?? undefined,
    }
}

type Props = {
    actions: CinemaAction[]
    loading?: boolean
}

export default function ActionsTab({ actions, loading }: Props) {
    const href = useCinemaHref()
    if (loading) {
        return (
            <div className="grid grid-cols-1 gap-3 px-4 pt-2 sm:grid-cols-2 md:gap-4 lg:grid-cols-3">
                {[0, 1, 2].map(i => (
                    <div key={i} className="overflow-hidden rounded-[16px] bg-white/[0.06]">
                        <div className="aspect-[16/9] w-full animate-pulse" />
                        <div className="space-y-2 p-3">
                            <div className="h-3 w-4/5 animate-pulse rounded bg-white/[0.08]" />
                            <div className="h-2.5 w-1/3 animate-pulse rounded bg-white/[0.06]" />
                        </div>
                    </div>
                ))}
            </div>
        )
    }

    if (actions.length === 0) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col items-center justify-center gap-4 py-24 text-white/30">
                <span className="text-[64px]">🎟</span>
                <p className="text-center" style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 20 }}>
                    Акций пока нет
                </p>
                <p className="text-[14px] text-center text-white/40">Следите за обновлениями</p>
            </motion.div>
        )
    }

    return (
        <div className="grid grid-cols-1 gap-3 px-4 pt-2 sm:grid-cols-2 md:gap-4 lg:grid-cols-3">
            {actions.map((action, index) => {
                const banner = actionAsBanner(action)
                return (
                    <motion.div
                        key={action.id}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: Math.min(index * 0.04, 0.24) }}
                        className="relative min-w-0">
                        <Link
                            to={href(actionPath(action, actions))}
                            state={{ pageTitle: action.title }}
                            className="group flex h-full flex-col overflow-hidden rounded-[16px] border border-white/10 bg-white/[0.04] transition-colors hover:border-white/20 md:rounded-[18px]">
                            <div className="relative aspect-[16/9] w-full shrink-0 overflow-hidden bg-white/[0.04]">
                                <img
                                    src={action.image}
                                    alt={action.title}
                                    className="size-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                                    loading="lazy"
                                />
                            </div>
                            <div className="flex flex-1 flex-col gap-1 p-2.5 md:p-3">
                                <h3
                                    className="line-clamp-2 text-white leading-snug"
                                    style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 13 }}>
                                    {action.title}
                                </h3>
                                {action.subtitle?.trim() ? (
                                    <p
                                        className="mt-auto line-clamp-2 text-[11px] leading-snug text-white/55 md:text-[12px]"
                                        style={{ fontFamily: BODY, fontWeight: 500 }}>
                                        {action.subtitle.trim()}
                                    </p>
                                ) : null}
                            </div>
                        </Link>
                        {hasBannerAdInfo(banner) && (
                            <div className="absolute right-1.5 top-1.5 z-10 scale-90 origin-top-right md:right-2 md:top-2 md:scale-100">
                                <BannerAdDisclosure banner={banner} />
                            </div>
                        )}
                    </motion.div>
                )
            })}
        </div>
    )
}
